<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Loader from "../../../components/widgets/loader";
import Forms from "../../../mixins/Forms";
import Tickets from "../../../mixins/Tickets";
import Dates from "../../../mixins/Dates";
import TicketCard from "../../../components/tickets/TicketCard";

let _ = require('lodash');
import moment from 'moment';

import axios from 'axios';
import SortByFilters from "./index/SortByFilters";
import AllIncompleteTasks from '../../../components/tickets/AllIncompleteTasks.vue';
import JSZip from 'jszip';

/**
 * View incident reports component
 */
export default {
  page: {
    title: "Incident Reports",
    meta: [{ name: "description", content: appConfig.description }]
  },
  mixins: [Forms, Tickets, Dates],
  components: { SortByFilters, Layout, PageHeader, Loader, TicketCard, AllIncompleteTasks },
  data() {
    return {
      title: "Incident Reports",
      items: [
        {
          text: "Incident Reports",
          href: "#"
        },
        {
          text: "All Incident Reports",
          active: true
        }
      ],

      serverErrors: [],
      isLoading: false,
      incidentReports: null,

      searchQueryIsDirty: false,
      searchQuery: '',
      filters: {
        sort_by: 'created_at',
        sort_order: 'DESC',
        results_per_page: 30,
        closed: null, // null = all, true = closed only, false = open only
        itg_included_activity: null, // null = all, true = included, false = not included
        layout: 'cards' // table or cards
      },
      
      // Date range filters
      dateRange: {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().endOf('month').format('YYYY-MM-DD')
      },
      
      // Date presets
      datePresets: [
        { label: 'This Month', start: () => moment().startOf('month'), end: () => moment().endOf('month') },
        { label: 'Last Month', start: () => moment().subtract(1, 'month').startOf('month'), end: () => moment().subtract(1, 'month').endOf('month') },
        // { label: 'This Quarter', start: () => moment().startOf('quarter'), end: () => moment().endOf('quarter') },
        // { label: 'Last Quarter', start: () => moment().subtract(1, 'quarter').startOf('quarter'), end: () => moment().subtract(1, 'quarter').endOf('quarter') },
        { label: 'This Year', start: () => moment().startOf('year'), end: () => moment().endOf('year') },
        // { label: 'Last Year', start: () => moment().subtract(1, 'year').startOf('year'), end: () => moment().subtract(1, 'year').endOf('year') }
      ]
    };
  },
  computed: {
    incidentReportResults() {
      if (this.incidentReports) {
        return this.incidentReports.data;
      }
      return [];
    }
  },
  methods: {
    determineSearchParams(page) {
      let params = {
        q: this.searchQuery,
        page: page,
        results_per_page: this.filters.results_per_page,
        sort_by: this.filters.sort_by,
        sort_order: this.filters.sort_order,
        start_date: this.dateRange.start_date,
        end_date: this.dateRange.end_date
      };

      if (this.filters.closed !== null) {
        params['closed'] = this.filters.closed;
      }

      if (this.filters.itg_included_activity !== null) {
        params['itg_included_activity'] = this.filters.itg_included_activity;
      }

      return params;
    },

    loadIncidentReports: _.debounce(function (page = 1) {
      let vm = this;
      vm.serverErrors = [];
      vm.isLoading = true;
      axios.post(API_BASE + '/tickets/search-incident-reports', vm.determineSearchParams(page)).then(r => {
        vm.incidentReports = r.data;
        vm.isLoading = false;
        vm.searchQueryIsDirty = false;
      }).catch(e => {
        console.log(e);
        vm.isLoading = false;
        vm.setErrors(e);
      });
    }),

    setDatePreset(preset) {
      this.dateRange.start_date = preset.start().format('YYYY-MM-DD');
      this.dateRange.end_date = preset.end().format('YYYY-MM-DD');
      this.loadIncidentReports();
    },

    setup() {
      // Load filters from local storage if exists
      let filters = localStorage.getItem('incident_reports_filters');
      if (filters) {
        this.filters = JSON.parse(filters);
      }

      // Load date range from local storage if exists
      let dateRange = localStorage.getItem('incident_reports_date_range');
      if (dateRange) {
        this.dateRange = JSON.parse(dateRange);
      }

      this.loadIncidentReports();
    },

    downloadReport(ticketId) {
      // Show loading state
      this.$bvToast.toast('Downloading PDF...', {
        title: 'Info',
        variant: 'info',
        solid: true
      });

      axios.get(API_BASE + `/tickets/${ticketId}/incident-report/download-pdf`, {
        responseType: 'blob',
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `incident_report_ticket_${ticketId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        // Clean up the URL object
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$bvToast.toast('PDF downloaded successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      }).catch(error => {
        console.error('Error downloading PDF:', error);
        this.$bvToast.toast('Failed to download PDF', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    },

    async downloadAllReports() {
      if (!this.incidentReportResults.length) {
        this.$bvToast.toast('No reports to download', {
          title: 'Info',
          variant: 'info',
          solid: true
        });
        return;
      }

      // Get all tickets that have incident reports
      const ticketsWithReports = this.incidentReportResults
        .filter(report => report.incident_report);

      if (ticketsWithReports.length === 0) {
        this.$bvToast.toast('No incident reports found to download', {
          title: 'Info',
          variant: 'info',
          solid: true
        });
        return;
      }

      // Show loading state
      this.$bvToast.toast(`Preparing ${ticketsWithReports.length} reports for download...`, {
        title: 'Info',
        variant: 'info',
        solid: true
      });

      try {
        const zip = new JSZip();
        const downloadPromises = [];

        // Download all reports and add them to the zip
        for (const ticket of ticketsWithReports) {
          const promise = axios.get(API_BASE + `/tickets/${ticket.id}/incident-report/download-pdf`, {
            responseType: 'blob',
          }).then(response => {
            const fileName = `incident_report_ticket_${ticket.id}.pdf`;
            zip.file(fileName, response.data);
            return fileName;
          }).catch(error => {
            console.error(`Error downloading report for ticket ${ticket.id}:`, error);
            return null;
          });
          
          downloadPromises.push(promise);
        }

        // Wait for all downloads to complete
        const downloadedFiles = await Promise.all(downloadPromises);
        const successfulDownloads = downloadedFiles.filter(file => file !== null);

        if (successfulDownloads.length === 0) {
          this.$bvToast.toast('Failed to download any reports', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          return;
        }

        // Generate the zip file
        this.$bvToast.toast('Generating zip file...', {
          title: 'Info',
          variant: 'info',
          solid: true
        });

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // Download the zip file
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `incident_reports_${moment().format('YYYY-MM-DD')}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        // Clean up the URL object
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$bvToast.toast(`Successfully downloaded ${successfulDownloads.length} reports in zip file`, {
          title: 'Success',
          variant: 'success',
          solid: true
        });

      } catch (error) {
        console.error('Error creating zip file:', error);
        this.$bvToast.toast('Failed to create zip file', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    }
  },
  watch: {
    '$route': 'setup',
    searchQuery: function () {
      this.searchQueryIsDirty = true;
      this.loadIncidentReports();
    },
    dateRange: {
      handler: function () {
        this.loadIncidentReports();
        // Save to local storage
        localStorage.setItem('incident_reports_date_range', JSON.stringify(this.dateRange));
      },
      deep: true
    },
    filters: {
      handler: function () {
        this.loadIncidentReports();
        // Save to local storage
        localStorage.setItem('incident_reports_filters', JSON.stringify(this.filters));
      },
      deep: true
    }
  },
  created() {
    this.setup();
  }
};
</script>

<template>
  <Layout>
    <all-incomplete-tasks></all-incomplete-tasks>

    <PageHeader :title="title" :items="items" />
    <errors :error-list="serverErrors"></errors>

    <div class="row">
      <div class="col-lg-9">
        <!-- Topbar: Search left, Sort right -->
        <div class="row mb-3">
          <div class="col-sm-4">
            <div class="search-box mr-2 mb-2 d-inline-block">
              <div class="position-relative">
                <input v-model="searchQuery" type="text" class="form-control" placeholder="Search..." />
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </div>
          <div class="col-sm-8 text-right">
            <sort-by-filters :filters="filters"></sort-by-filters>
            <button type="button" class="btn btn-itg-dark ml-3"
              @click="downloadAllReports"
              :disabled="!incidentReportResults.length">
              Download All Reports
            </button>
          </div>
        </div>
        <!-- / Topbar -->

        <!-- Is Loading -->
        <div v-if="isLoading || searchQueryIsDirty" class="card">
          <div class="card-body text-center">
            <b-spinner class="m-2" variant="itg-dark" role="status"></b-spinner>
          </div>
        </div>
        <!-- / Is Loading -->

        <!-- Table view -->
        <div v-if="filters.layout === 'table' && !(isLoading || searchQueryIsDirty)" class="card">
          <div class="card-body">
            <div class="table-responsive mb-0">
              <table class="table mb-0 table-centered">
                <thead class="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Subject</th>
                    <th>Ticket Status</th>
                    <th>Incident Report Status</th>
                    <th>Priority</th>
                    <th>Difficulty</th>
                    <th>Ticket Created</th>
                    <th>Agent</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="incidentReport in incidentReportResults" :key="incidentReport.id">
                    <th scope="row">{{ incidentReport.id }}</th>
                    <td>{{ incidentReport.subject }}</td>
                    <td>{{ getHumanReadableTicketStatus(incidentReport.status) }}</td>
                    <td>
                      <span v-if="incidentReport.incident_report" 
                        :class="['badge', incidentReport.incident_report.closed_at ? 'badge-danger' : 'badge-success']">
                        {{ incidentReport.incident_report.closed_at ? 'Closed' : 'Open' }}
                      </span>
                      <span v-else class="badge badge-warning">Not Started</span>
                    </td>
                    <td>{{ getHumanReadableTicketPriority(incidentReport.priority) }}</td>
                    <td>{{ incidentReport.difficulty }}</td>
                    <td>{{ displayTimeOpen(incidentReport.received_at) }}</td>
                    <td>{{ incidentReport.agent ? incidentReport.agent.name : '-' }}</td>
                    <td class="text-right" style="white-space: nowrap;">
                      <button type="button" class="btn btn-itg btn-itg btn-sm mr-2"
                        @click="$router.push('/tickets/' + incidentReport.id)">
                        View
                      </button>
                      <button type="button" class="btn btn-itg-dark btn-sm"
                        @click="downloadReport(incidentReport.id)"
                        :disabled="!incidentReport.incident_report">
                        Download
                      </button>
                    </td>
                  </tr>
                  <tr v-if="incidentReportResults.length === 0">
                    <th scope="row" colspan="9">
                      <span class="text-danger">No results</span>
                    </th>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- / Table view -->

        <!-- Card view -->
        <div v-if="filters.layout === 'cards' && !(isLoading || searchQueryIsDirty)">
          <div class="card-results-wrapper">
            <ticket-card v-for="incidentReport in incidentReportResults" :key="incidentReport.id" :ticket="incidentReport" :incident_report="incidentReport.incident_report"></ticket-card>
            <div class="card" v-if="incidentReportResults.length === 0">
              <div class="card-body">
                <b class="text-danger">No results</b>
              </div>
            </div>
          </div>
        </div>
        <!--- / Cards -->

        <pagination v-if="!(isLoading || searchQueryIsDirty) && incidentReports" :data="incidentReports" :limit="8"
          @pagination-change-page="loadIncidentReports"></pagination>
      </div>
      <div class="col-lg-3">
        <div class="card mb-3">
          <div class="card-body p-4">
            <!-- Quick Presets -->
            <div class="form-group mb-3">
              <label>Quick Presets</label>
              <div>
                <button 
                  v-for="preset in datePresets" 
                  :key="preset.label"
                  @click="setDatePreset(preset)"
                  class="btn btn-itg btn-sm mr-2 mb-2"
                >
                  {{ preset.label }}
                </button>
              </div>
            </div>
            <!-- / Quick Presets -->
            <!-- Date Range -->
            <div class="form-group mb-3">
              <label for="start_date">Start Date</label>
              <input 
                v-model="dateRange.start_date" 
                type="date" 
                class="form-control" 
                id="start_date"
              />
            </div>
            <div class="form-group mb-3">
              <label for="end_date">End Date</label>
              <input 
                v-model="dateRange.end_date" 
                type="date" 
                class="form-control" 
                id="end_date"
              />
            </div>
            <!-- / Date Range -->
          </div>
        </div>
        <div class="card">
          <div class="card-body p-4">
            <!-- Results per page -->
            <div class="form-group mb-3">
              <label for="results_per_page">Results To Show</label>
              <v-select v-model="filters.results_per_page" :clearable="false"
                :options="[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]"></v-select>
            </div>
            <!-- / Results per page -->

            <!-- Closed Status -->
            <div class="form-group mb-3">
              <label for="closed">Incident Report Status</label>
              <v-select v-model="filters.closed" label="label" :reduce="option => option.value"
                :clearable="false" :options="[
                  { value: null, label: 'All' }, 
                  { value: false, label: 'Open Only' }, 
                  { value: true, label: 'Closed Only' }
                ]"></v-select>
            </div>
            <!-- / Closed Status -->

            <!-- ITG Included Activity -->
            <div class="form-group mb-3">
              <label for="itg_included_activity">ITG Included Activity</label>
              <v-select v-model="filters.itg_included_activity" label="label" :reduce="option => option.value"
                :clearable="false" :options="[
                  { value: null, label: 'All' }, 
                  { value: true, label: 'Included' }, 
                  { value: false, label: 'Not Included' }
                ]"></v-select>
            </div>
            <!-- / ITG Included Activity -->

            <!-- Layout -->
            <div class="form-group mb-3">
              <label for="layout">Layout</label>
              <v-select v-model="filters.layout" label="label" :reduce="option => option.value" :clearable="false"
                :options="[{ value: 'cards', label: 'Cards' }, { value: 'table', label: 'Table' }]"></v-select>
            </div>
            <!-- / Layout -->
          </div>
        </div>
      </div>
    </div>
    <!-- end row -->
  </Layout>
</template>

<style scoped>
.main-content {
  overflow: visible;
}
</style> 